import config from 'config';

export interface BundleComponent {
  componentProductId?: number | string;
  groupCode?: string;
  color?: string;
}

export interface CardItem {
  id?: string | number;
  type?: string;
  label?: string;
  imageFilename?: string;
  container?: { name?: string; data?: { id?: string | number } };
  options?: { customVars?: Record<string, unknown> };
  effectiveCustomVars?: Record<string, unknown>;
  span?: { rows?: number; columns?: number };
  productTypeId?: number;
  componentProductIdObjectList?: BundleComponent[] | null;
}

// Read a string config value safely; returns null if missing or unset.
const getStringConfig = (key: string): string | null => {
  try {
    const value = config.get(key) as string;
    return value || null;
  } catch {
    return null;
  }
};

// Replace `{token}` placeholders in a template with values from a map. Each
// substitution value is URL-encoded so callers don't need to remember to do
// it themselves. Use `{token-raw}` (or any non-string value) when you've
// pre-encoded the value (e.g. JSON-stringified preview blobs).
const fillTemplate = (
  template: string,
  values: Record<string, string | number>
): string =>
  Object.entries(values).reduce(
    (acc, [key, value]) =>
      acc.replaceAll(`{${key}}`, encodeURIComponent(String(value))),
    template
  );

// Derive the CMS host from the shared editUrlTemplate config. Picks up the
// env-specific CMS instance (dev/qa → cmsqa1, staging → stage, prod →
// techstyle.net). Do NOT use urlTemplate here — in prod, images are served
// from the CDN (us-cdn.justfab.com), which is a different host than the CMS.
const getCmsOrigin = (): string | null => {
  const template = getStringConfig('public.assets.editUrlTemplate');
  if (!template) {
    return null;
  }
  try {
    return new URL(template).origin;
  } catch {
    return null;
  }
};

export const resolveAssetEditUrl = (item: CardItem): string | null => {
  const origin = getCmsOrigin();
  if (!origin || item?.id === undefined || item?.id === null) {
    return null;
  }
  return `${origin}/index.cfm?action=assets.reviewAsset_v2&assets_id=${item.id}`;
};

export const resolveContainerEditUrl = (item: CardItem): string | null => {
  const origin = getCmsOrigin();
  const containerId = item?.container?.data?.id;
  if (!origin || containerId === undefined || containerId === null) {
    return null;
  }
  return `${origin}/index.cfm?action=assets.setupContainer&asset_container_id=${containerId}`;
};

// Product console URL from `public.products.editUrlTemplate`. Brand- and
// env-specific; template contains the correct host plus an `{id}` placeholder.
export const resolveProductEditUrlById = (
  id: string | number | null | undefined
): string | null => {
  if (id === undefined || id === null) {
    return null;
  }
  const template = getStringConfig('public.products.editUrlTemplate');
  if (!template) {
    return null;
  }
  return template.replace('{id}', String(id));
};

// Convenience wrapper for the master product id on a CardItem.
export const resolveProductEditUrl = (item: CardItem): string | null =>
  resolveProductEditUrlById(item?.id);

// ES7 product index URL template. Different per env; resolved from
// `public.es.productUrlTemplate` which switches between dev/qa and prod hosts.
export const resolveEsProductUrl = (item: CardItem): string | null => {
  if (item?.id === undefined || item?.id === null) {
    return null;
  }
  const template = getStringConfig('public.es.productUrlTemplate');
  if (!template) {
    return null;
  }
  return template.replace('{id}', String(item.id));
};

// Constructor.io apiKey is per brand + tld and lives under
// public.constructorio.apiKeys (resolved by whitelabel config). Returns null
// when the apiKey can't be resolved (e.g. unknown tld).
export const getCioApiKey = (tld: string | null | undefined): string | null => {
  if (!tld) {
    return null;
  }
  try {
    const apiKeys = config.get('public.constructorio.apiKeys') as Record<
      string,
      string
    >;
    return apiKeys?.[tld] || null;
  } catch {
    return null;
  }
};

export const buildCioInteractUrl = (
  apiKey: string,
  query: string | number
): string | null => {
  const template = getStringConfig(
    'public.constructorio.dashboard.interactUrlTemplate'
  );
  if (!template) {
    return null;
  }
  return fillTemplate(template, { apiKey, query });
};

export const buildCioCollectionEditUrl = (
  apiKey: string,
  collectionId: string
): string | null => {
  const template = getStringConfig(
    'public.constructorio.dashboard.collectionEditUrlTemplate'
  );
  if (!template) {
    return null;
  }
  return fillTemplate(template, { apiKey, collectionId });
};

export const buildCioSearchandizeUrl = (
  apiKey: string,
  filterName: string,
  filterValue: string
): string | null => {
  const template = getStringConfig(
    'public.constructorio.dashboard.searchandizeUrlTemplate'
  );
  if (!template) {
    return null;
  }
  // The CIO dashboard expects a JSON-encoded `preview` query param carrying
  // the same filterName / filterValue / indexKey it's deep-linking to.
  const preview = JSON.stringify({
    filterName,
    filterValue,
    indexKey: apiKey,
  });
  return fillTemplate(template, {
    apiKey,
    filterName,
    filterValue,
    preview,
  });
};
